import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { SectionLabel, Headline, Field, Stamp } from "@/components/Doc";

const TIER_LABEL = { standard: "Standard", tradecraft: "Tradecraft", full: "Full" };
const money = (n) => `$${Number(n).toLocaleString("en-US")}`;

const Line = ({ k, v, strong, testId }) => (
  <div className="flex justify-between py-2 border-b border-rule/60">
    <span className="t-label text-olive">{k}</span>
    <span className={`t-mono ${strong ? "font-bold text-lg" : ""}`} data-testid={testId}>{v}</span>
  </div>
);

const ReviewCheckout = () => {
  const [f, setF] = useState({ name: "", email: "", company: "" });
  const [busy, setBusy] = useState(false);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/checkout/findings-review", { ...f, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatError(err)); setBusy(false); }
  };
  return (
    <form onSubmit={go} className="bg-paper border border-carbon p-7 sm:p-10 shadow-[6px_6px_0_0_#D9C9A8] flex flex-col gap-6 max-w-xl" data-testid="review-checkout-form">
      <div><span className="t-label text-olive">Section C — Entry</span><h2 className="t-head text-3xl mt-2">The Findings Review — $1,500</h2></div>
      <p className="t-body text-base text-olive">Ninety minutes, remote, with a written memorandum. Credited in full against any installation booked within ninety days. One-time payment; on success we book the call.</p>
      <Field label="Name" name="name" value={f.name} onChange={set} required testId="review-name" />
      <Field label="Work email" name="email" type="email" value={f.email} onChange={set} required testId="review-email" />
      <Field label="Company" name="company" value={f.company} onChange={set} testId="review-company" />
      <button className="btn btn-stamp self-start" disabled={busy} data-testid="review-pay-btn">{busy ? "Opening…" : "Pay and Book"}</button>
    </form>
  );
};

export default function Checkout() {
  const [params] = useSearchParams();
  const isReview = params.get("product") === "findings-review";
  const tier = TIER_LABEL[params.get("tier")] ? params.get("tier") : "tradecraft";
  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="checkout-page">
      <SectionLabel right="Form LH-31">Doc. 12 — Checkout</SectionLabel>
      {isReview ? <div className="mt-10"><ReviewCheckout /></div> : <WatchCheckout tier={tier} />}
    </main>
  );
}

const WatchCheckout = ({ tier }) => {
  const [f, setF] = useState({ legal_name: "", location_count: 1, contact_name: "", work_email: "", phone: "" });
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });

  useEffect(() => {
    const n = Math.max(1, parseInt(f.location_count) || 1);
    api.get("/config/quote", { params: { tier, locations: n } }).then((r) => setQ(r.data)).catch(() => setQ(null));
  }, [tier, f.location_count]);

  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/checkout/watch", { ...f, tier, location_count: parseInt(f.location_count), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatError(err)); setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-6">
          <Headline as="h1" className="text-4xl sm:text-5xl">WATCH — {TIER_LABEL[tier]}</Headline>
          <p className="t-body text-lg mt-4 text-olive">Continuous testing, per location, per month. <Link to="/pricing" className="underline underline-offset-4 hover:text-stamp" data-testid="change-tier-link">Change tier</Link></p>
          <form onSubmit={go} className="mt-8 bg-paper border border-carbon p-7 sm:p-10 shadow-[6px_6px_0_0_#D9C9A8] flex flex-col gap-6" data-testid="watch-checkout-form">
            <Field label="Company legal name" name="legal_name" value={f.legal_name} onChange={set} required testId="co-legal-name" />
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Location count" name="location_count" type="number" value={f.location_count} onChange={set} required testId="co-location-count" />
              <Field label="Primary contact name" name="contact_name" value={f.contact_name} onChange={set} required testId="co-contact-name" />
              <Field label="Work email" name="work_email" type="email" value={f.work_email} onChange={set} required testId="co-work-email" />
              <Field label="Direct phone" name="phone" type="tel" value={f.phone} onChange={set} required testId="co-phone" />
            </div>
            <p className="t-label text-rule">Billing details are collected on the next screen by Stripe.</p>
            <button className="btn btn-stamp self-start" disabled={busy || !q || q.by_contract} data-testid="co-pay-btn">{busy ? "Opening…" : "Store Card and Continue"}</button>
            <p className="t-body text-base text-olive leading-snug" data-testid="co-billing-note">Your card is stored now. Billing begins when testing begins. After purchase you'll complete a short authorization step — we don't contact anyone at your company until an officer has authorized the scope in writing and we've verified it. This usually takes three to seven days.</p>
          </form>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <div className="bg-manila border border-rule p-7 relative" data-testid="quote-box">
            <div className="absolute top-4 right-4"><Stamp color="carbon" rotate={4} size="sm">Pro Forma</Stamp></div>
            <span className="t-label text-olive">Computed monthly figure</span>
            {q && !q.by_contract ? (
              <div className="mt-6">
                <Line k={`${TIER_LABEL[tier]} × ${q.location_count} location${q.location_count > 1 ? "s" : ""}`} v={money(q.base_monthly)} testId="quote-base" />
                <Line k={`Volume discount ${q.discount_rate ? `(${Math.round(q.discount_rate * 100)}%)` : ""}`} v={q.discount_monthly ? `− ${money(q.discount_monthly)}` : "—"} testId="quote-discount" />
                <Line k="Total per month" v={money(q.total_monthly)} strong testId="quote-total" />
                <p className="t-label text-rule mt-4">First invoice raised on activation. Tax, if applicable, is calculated by Stripe at checkout.</p>
              </div>
            ) : q && q.by_contract ? (
              <p className="t-body text-lg mt-6" data-testid="quote-contract">Twenty-five or more locations are priced by contract. <Link to="/contact?interest=memorandum" className="underline underline-offset-4">Request the engagement memorandum.</Link></p>
            ) : <p className="t-label text-rule mt-6">Computing…</p>}
          </div>
        </div>
    </div>
  );
};
